// @flow

import type { PlayerContainer, PlayerData, PlayerState, Show, VideoData } from './store';

import PassportUser from '../User/PassportUser';
let user = new PassportUser();

export function nextVideoInContainer(container: PlayerContainer, currentId: string): ?VideoData {
  let { fullLength, preview, clips } = container;

  // If the current video is the full length, then it is no following video
  // in the container
  if (fullLength && fullLength.id === currentId) {
    return null;
  }

  // By default go back to the full video
  let next = fullLength;

  // If the current id is the preview, the next video is always the full length
  if (preview && preview.id === currentId) {
    return next;
  }

  // Check for a next clip
  let currentIndex = clips.map(c => c.id).indexOf(currentId);

  // If there is a next clip then load that clip
  if (currentIndex < clips.length - 1) {
    next = clips[currentIndex + 1];
  }

  return next;
}

export function getPlayingVideo(container: PlayerContainer, attr: string, value: string): ?VideoData {
  let { fullLength, preview, clips } = container;

  if (fullLength && fullLength[attr] === value) {
    return fullLength;
  }

  if (preview && preview[attr] === value) {
    return preview;
  }

  let cId = clips.map(c => c[attr]).indexOf(value);

  return cId > -1 && clips[cId] || null;
}

export function getPlayingVideoById(container: PlayerContainer, id: string): ?VideoData {
  return getPlayingVideo(container, 'id', id);
}

export function getPlayingVideoBySlug(container: PlayerContainer, slug: string): ?VideoData {
  return getPlayingVideo(container, 'slug', slug);
}

export function getDefaultVideo(container: PlayerContainer): ?VideoData {
  let { fullLength, preview, clips } = container;

  if (fullLength) {
    return fullLength;
  }

  if (preview) {
    return preview;
  }

  if (clips.length > 0) {
    return clips[0];
  }

  return null;
}

export function videoDataToPlayerData(video: VideoData, autoplay: boolean = false): PlayerData {
  return {
    id: video.id,
    tpMediaId: video.tpMediaId,
    position: 0,
    autoplay: autoplay
  };
}

export function watchableToVideoData(response: Object): PlayerContainer {
  let { show, season, episode, special, full_length, clips, preview } = response;

  // Given a hash of availability windows and a passport level, computes
  // if the video is watchable for that level
  let isWatchable = function isWatchable(avails, key) {
    let window = avails[key],
      start = window['start'] * 1000,
      end = !window['end'] ? null : window['end'] * 1000,
      current = Date.now();

    return start <= current && (end === null || end > current);
  };

  // Given a hash of availability windows and a passport level, computes
  // if the video is going to be expiring soon
  let isExpiring = function isExpiring(avails, key) {
    let window = avails[key],
      end = !window['end'] ? null : window['end'] * 1000,
      windowEnd = Date.now() + 7 * 24 * 60 * 60 * 1000;

    return end !== null && end < windowEnd;
  };

  let videoToVideoData = function (video): VideoData {
    return {
      id: video.id,
      airdate: video.premiered_on,
      tpMediaId: video.tp_media_id,
      title: video.title,
      language: video.language || null,
      slug: video.slug,
      shortDescription: video.description_short,
      extendedDescription: video.description_extended ? video.description_extended : video.description_long,
      isExpiring: isExpiring(video.availabilities, user.getAccessLevelKey()),
      isWatchable: isWatchable(video.availabilities, user.getAccessLevelKey()),
      isPassport: video.is_passport,
      minimumPassportLevel: video.minimum_access_level,
      displaySeason: !!show && show.ordinal_season && season,
      season: season ? season.ordinal : null,
      episodeWithinSeason: (!!show && show.display_episode_number && episode) ? episode.ordinal : null,
      mezzanine: video.mezzanine,
      shareURLs: [],
      viralPlayerEmbeddedCode: '',
      relatedBlogPost: {
        title: '',
        url: '',
      },
      fetchingVideo: false,
      fetchedVideo: true,
      error: null,
    };
  };

  // Check the slug and use that video instead of just the first preview if it is set.
  var slug = getVideoSlug(window.location.pathname), actualPreviews = [];

  if (preview.length > 0 && typeof preview[0] !== "string") {
    actualPreviews = preview.filter(function (val) {
      if (val.slug === slug) {
        return true;
      }

      return false;
    });
  } else {
    actualPreviews.push(null);
  }

  if (actualPreviews.length === 0) {
    actualPreviews.push(preview[0]);
  }

  return {
    fullLength: full_length && videoToVideoData(full_length) || null,
    preview: actualPreviews[0] === null ? null : videoToVideoData(actualPreviews[0]),
    clips: clips.filter(c => typeof c !== "string").map(videoToVideoData)
  }
}

export function isRoot(slug: string): boolean {
  return window.location.href.search(new RegExp(`/${slug}/$`)) !== -1;
}

export function isSpecialPath(path: string): boolean {
  return path.search(/\/schedule\/?$/) !== -1 || path.search(/\/about\/?$/) !== -1;
}

export function getBasicVideoTrackingLabel(show: Show, player: PlayerState): ?string {
  return getVideoTrackingLabel(show, player, player.currentlyPlaying.id);
}

export function getVideoTrackingLabel(show: Show, player: PlayerState, id: string): ?string {
  let video = getPlayingVideo(player.container, 'id', id);

  if (video) {
    let label = [
      show ? show.title : 'TITLE_MISSING',
      player.container.fullLength ? player.container.fullLength.title : 'None',
      video.title,
      video.tpMediaId
    ].join(' | ');

    if (video.isPassport) {
      label += ' [Passport]';
    }

    return label;
  }

  return null;
}

export function getVideoSlug(path: string) {
  let matches = path.match(/\/[a-zA-Z0-9\-]+\/video\/([a-zA-Z0-9\-]+)\//);

  if (matches && matches.length > 1) {
    return matches[1];
  }

  return '';
}